import { useLocalization } from "@fluent/react"
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined"
import Autocomplete from "@mui/material/Autocomplete"
import InputAdornment from "@mui/material/InputAdornment"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { unwrapResult } from "@reduxjs/toolkit"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useAppDispatch } from "../../../../app/hooks"
import { loadTemplatesForStructureNode, StructureNodeDocumentDto } from "../../documentsQmsApi"

interface QMSDocumentTemplatesProps {
    nodeId?: string | null
    value?: string | null
    onChange: (value: string, template?: StructureNodeDocumentDto) => void
    disabled?: boolean
    error?: boolean
    helperText?: React.ReactNode
}

export const QMSDocumentTemplates = ({
    nodeId,
    value,
    onChange,
    disabled = false,
    error = false,
    helperText,
}: QMSDocumentTemplatesProps) => {
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()

    const [options, setOptions] = useState<StructureNodeDocumentDto[]>([])
    const [loading, setLoading] = useState(false)

    const selectedTemplate = useMemo(
        () => options.find(option => String(option.documentId) === String(value)) ?? null,
        [options, value],
    )

    const loadOptions = useCallback(async () => {
        if (!nodeId) {
            setOptions([])
            return
        }

        setLoading(true)
        try {
            const result = await dispatch(loadTemplatesForStructureNode({ nodeId, includeParents: false })).then(unwrapResult)
            setOptions(result)
        } catch (error) {
            console.error(error)
            setOptions([])
        } finally {
            setLoading(false)
        }
    }, [dispatch, nodeId])

    useEffect(() => {
        loadOptions()
    }, [loadOptions])

    useEffect(() => {
        if (!value) {
            return
        }

        if (!options.some(option => String(option.documentId) === String(value))) {
            onChange("")
        }
    }, [options, value, onChange])

    return (
        <Autocomplete<StructureNodeDocumentDto, false, false, false>
            options={options}
            value={selectedTemplate}
            onChange={(_, option) => onChange(option ? String(option.documentId) : "", option ?? undefined)}
            getOptionLabel={option => `${option.documentNumber || "-"} - ${option.title}`}
            isOptionEqualToValue={(option, selectedOption) => option.documentId === selectedOption.documentId}
            loading={loading}
            disabled={disabled || !nodeId}
            noOptionsText={l10n.getString("qms-document-templates-empty", null, "Brak dostępnych szablonów dla wybranego noda")}
            renderOption={(props, option) => (
                <li {...props}>
                    <div style={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                            {option.documentNumber || "-"}
                        </Typography>
                        <Typography variant="body2" noWrap>
                            {option.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {option.documentType}
                        </Typography>
                    </div>
                </li>
            )}
            renderInput={params => (
                <TextField
                    {...params}
                    required
                    error={error}
                    helperText={helperText}
                    label={l10n.getString("document-template-select", null, "Wybierz szablon")}
                    placeholder={l10n.getString("qms-document-templates-placeholder", null, "Wskaż szablon dokumentu")}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <>
                                <InputAdornment position="start">
                                    <DescriptionOutlinedIcon fontSize="small" color="primary" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    )
}